import React, { useState, useMemo } from "react";
import type { IgnoredPerSessionProof } from "../../../types/core";

interface IgnoredProofViewProps {
  data: IgnoredPerSessionProof[];
  setterName: string;
  timezone: string;
  onConversationClick: (
    conversationId: string,
    contactName: string,
    leadInstagram: string | null,
    highlightTimestamps?: { highlight: string; anchor?: string }
  ) => void;
}

interface SessionGroup {
  sessionNumber: number;
  sessionStart: string;
  sessionEnd: string;
  conversations: IgnoredPerSessionProof[];
}

function formatTime(iso: string, timezone: string): string {
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: timezone,
  });
}

function formatDate(iso: string, timezone: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: timezone,
  });
}

export const IgnoredProofView: React.FC<IgnoredProofViewProps> = ({
  data,
  setterName,
  timezone,
  onConversationClick,
}) => {
  const [expandedSessions, setExpandedSessions] = useState<Set<number>>(new Set());

  // Group by session (memoized to avoid re-sorting on every render)
  const sessions = useMemo(() => {
    const sessionMap = new Map<number, SessionGroup>();
    data.forEach((row) => {
      if (!sessionMap.has(row.session_number)) {
        sessionMap.set(row.session_number, {
          sessionNumber: row.session_number,
          sessionStart: row.session_start,
          sessionEnd: row.session_end,
          conversations: [],
        });
      }
      sessionMap.get(row.session_number)!.conversations.push(row);
    });
    return Array.from(sessionMap.values()).sort((a, b) => a.sessionNumber - b.sessionNumber);
  }, [data]);

  if (sessions.length === 0) {
    return (
      <p className="token-14 text-glass-muted py-4 text-center">
        No ignored conversations for {setterName}.
      </p>
    );
  }

  const toggleSession = (num: number) => {
    setExpandedSessions((prev) => {
      const next = new Set(prev);
      if (next.has(num)) {
        next.delete(num);
      } else {
        next.add(num);
      }
      return next;
    });
  };

  return (
    <div>
      <h4 className="token-14 font-semibold text-glass-secondary mb-3">{setterName}</h4>
      <div className="space-y-2">
        {sessions.map((session) => {
          const isExpanded = expandedSessions.has(session.sessionNumber);
          return (
            <div
              key={session.sessionNumber}
              className="border border-black/[0.08] rounded-lg overflow-hidden"
            >
              <button
                onClick={() => toggleSession(session.sessionNumber)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-black/[0.03] hover:bg-black/[0.05] transition-colors"
              >
                <div className="flex items-center gap-3 token-14">
                  <span className="text-glass-muted">#{session.sessionNumber}</span>
                  <span className="font-medium text-glass-secondary">
                    {formatDate(session.sessionStart, timezone)}{" "}
                    {formatTime(session.sessionStart, timezone)} —{" "}
                    {formatTime(session.sessionEnd, timezone)}
                  </span>
                  <span className="text-ds-red font-medium">
                    {session.conversations.length} ignored
                  </span>
                </div>
                <svg
                  className={`w-4 h-4 text-glass-muted transition-transform ${isExpanded ? "rotate-180" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {isExpanded && (
                <div className="px-4 py-2 space-y-1">
                  {session.conversations.map((conv) => (
                    <div
                      key={conv.conversation_id}
                      className="flex items-center gap-3 px-2 py-1.5 rounded hover:bg-black/[0.03] cursor-pointer group"
                      onClick={() =>
                        onConversationClick(
                          conv.conversation_id,
                          conv.contact_name || "Unknown",
                          conv.lead_instagram,
                          { highlight: conv.lead_msg_at }
                        )
                      }
                    >
                      <div className="flex-1 min-w-0">
                        <span className="token-14 font-medium text-glass truncate">
                          {conv.contact_name || "Unknown"}
                        </span>
                        {conv.lead_instagram && (
                          <span className="token-11 text-glass-muted ml-2">
                            @{conv.lead_instagram}
                          </span>
                        )}
                      </div>
                      <span className="token-11 text-glass-muted">
                        {formatTime(conv.lead_msg_at, timezone)}
                      </span>
                      {conv.was_replied ? (
                        <span className="token-11 bg-ds-green-bg text-ds-green px-1.5 py-0.5 rounded">
                          Replied later
                        </span>
                      ) : (
                        <span className="token-11 bg-ds-red/15 text-ds-red px-1.5 py-0.5 rounded">
                          Never replied
                        </span>
                      )}
                      <svg
                        className="w-4 h-4 text-glass-inactive group-hover:text-glass-muted shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="token-11 text-glass-muted mt-2">
        {data.length} ignore events across {sessions.length} sessions
      </p>
    </div>
  );
};
